"use client";

import React, { useState, useEffect } from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogPanel,
  DialogBackdrop,
} from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { ScreenerAPI, type Asset } from "@/services/api";
import {
  fetchScreenerWatchlists,
  type Watchlist,
} from "@/services/api/screener";
import { useDebouncedValue } from "@/hooks/useDebounce";
import { StatusIndicator } from "@/components/indicators/StatusIndicator";
import { useAssetManager } from "@/components/playground/hooks/useAssetManager";

interface AssetSearchComboboxProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAsset: (asset: Asset) => void;
  selectedAssets: string[];
  includeWatchlists?: boolean;
  onSelectWatchlist?: (watchlist: Watchlist) => void;
  selectedWatchlistId?: string;
}

// Helper function to get resource ID safely
function getAssetResourceId(asset: Asset): string | undefined {
  // Type assertion to any to work around potential type mismatch
  const assetAny = asset as any;

  // Check multiple possible locations for resource_id
  if (assetAny?.metadata?.resource_id) {
    return assetAny.metadata.resource_id;
  }

  // Check if resource_id is directly on the asset
  if (assetAny?.resource_id) {
    return assetAny.resource_id;
  }

  // Check if id is available
  if (assetAny?.id) {
    return assetAny.id;
  }

  return undefined;
}

export default function AssetSearchCombobox({
  isOpen,
  onClose,
  onSelectAsset,
  selectedAssets,
  includeWatchlists = false,
  onSelectWatchlist,
  selectedWatchlistId,
}: AssetSearchComboboxProps) {
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<Asset[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [watchlistResults, setWatchlistResults] = useState<Watchlist[]>([]);

  const handleClose = () => {
    onClose();
  };

  // Use asset manager for consistent color mappings
  const { assetColors } = useAssetManager({
    initialAssets: selectedAssets,
    allowAssetToggle: false,
  });

  // Use debounced search term
  const debouncedQuery = useDebouncedValue(query, 300);

  // Search for assets when query changes
  useEffect(() => {
    const searchAssets = async () => {
      if (!debouncedQuery || debouncedQuery.length < 2) {
        setSearchResults([]);
        setWatchlistResults([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const results = await ScreenerAPI.fetchScreenerSearch(debouncedQuery);
        const items = results?.items || [];
        setSearchResults(items as unknown as Asset[]);

        // Optionally search watchlists by name (client-side filter)
        if (includeWatchlists) {
          const allWl = await fetchScreenerWatchlists();
          const q = debouncedQuery.toLowerCase();
          const filtered = (allWl || []).filter((wl) =>
            wl.name?.toLowerCase().includes(q)
          );
          setWatchlistResults(filtered);
        }
      } catch {
        setError("Failed to search assets. Please try again.");
        setSearchResults([]);
        setWatchlistResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    searchAssets();
  }, [debouncedQuery]);

  // Clear query when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setQuery("");
    }
  }, [isOpen]);

  // Function to render additional asset details
  const renderAssetDetails = (asset: Asset) => {
    const details = [];

    if (asset.data.fund_size) details.push(`Size: ${asset.data.fund_size}`);
    if (asset.data.currency) details.push(`Currency: ${asset.data.currency}`);
    if (asset.data.geographic_exposure)
      details.push(`Region: ${asset.data.geographic_exposure}`);
    if (asset.data.asset_allocation)
      details.push(`Type: ${asset.data.asset_allocation}`);

    if (details.length === 0) return null;

    return (
      <div className="text-xs text-gray-500 dark:text-gray-400">
        {details.join(" • ")}
      </div>
    );
  };

  return (
    <div>
      <Dialog className="relative z-50" open={isOpen} onClose={handleClose}>
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/25 transition-opacity dark:bg-gray-900/50 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto p-4 sm:p-6 md:p-20">
          <DialogPanel
            transition
            className="mx-auto max-w-xl transform divide-y divide-gray-100 dark:divide-gray-700 overflow-hidden rounded-xl bg-white dark:bg-gray-800 ring-1 shadow-2xl ring-black/5 transition-all data-closed:scale-95 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
          >
            <Combobox onChange={(asset: Asset) => onSelectAsset(asset)}>
              <div className="grid grid-cols-1">
                <ComboboxInput
                  autoFocus
                  className="col-start-1 row-start-1 h-12 w-full pr-4 pl-11 text-base text-gray-900 dark:text-white bg-white dark:bg-gray-800 outline-hidden placeholder:text-gray-400 dark:placeholder:text-gray-500 sm:text-sm"
                  placeholder="Search for assets..."
                  onChange={(event) => setQuery(event.target.value)}
                  onBlur={() => {}} // Keep the query when blurred
                />
                {isLoading ? (
                  <div className="col-start-1 row-start-1 ml-4 flex items-center h-12">
                    <StatusIndicator status="in_progress" />
                  </div>
                ) : (
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className="pointer-events-none col-start-1 row-start-1 ml-4 h-5 w-5 self-center text-gray-400 dark:text-gray-500"
                    aria-hidden="true"
                  />
                )}
              </div>

              {error && (
                <div className="p-4 text-sm text-red-500 dark:text-red-400">
                  {error}
                </div>
              )}

              {includeWatchlists && watchlistResults.length > 0 && (
                <div className="max-h-60 overflow-y-auto py-2 text-sm">
                  <div className="px-4 py-2 text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                    Watchlists
                  </div>
                  {watchlistResults.map((wl) => (
                    <div
                      key={wl.id}
                      className={`cursor-pointer px-4 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-500/20 rounded ${selectedWatchlistId === wl.id ? "bg-indigo-50 dark:bg-indigo-500/20" : ""}`}
                      onClick={() => {
                        onSelectWatchlist?.(wl);
                        handleClose();
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {wl.name}
                        </span>
                        {typeof wl.item_count === "number" && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {wl.item_count}
                          </span>
                        )}
                      </div>
                      {wl.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {wl.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {searchResults.length > 0 && (
                <ComboboxOptions
                  static
                  className="max-h-72 scroll-py-2 overflow-y-auto py-2 text-sm text-gray-800 dark:text-gray-200"
                >
                  {searchResults.map((asset, index) => {
                    return (
                      <ComboboxOption
                        key={`${getAssetResourceId(asset)}-${index}`}
                        value={asset}
                        className="cursor-pointer px-4 py-2 select-none data-focus:bg-indigo-50 data-focus:text-white dark:data-focus:bg-indigo-500 data-focus:outline-hidden"
                      >
                        <div className="flex flex-col">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div
                                className="h-3 w-3 rounded-full mr-2"
                                style={{
                                  backgroundColor: selectedAssets.includes(
                                    getAssetResourceId(asset) || ""
                                  )
                                    ? assetColors[
                                        getAssetResourceId(asset) || ""
                                      ] || "#3b82f6"
                                    : "#d1d5db",
                                }}
                              />
                              <span className="font-medium text-gray-900 dark:text-white">
                                {asset.display_name}
                              </span>
                              <span className="ml-1 text-xs text-gray-600 dark:text-gray-300 opacity-70">
                                ({asset.resource_type})
                              </span>
                            </div>
                          </div>
                          {renderAssetDetails(asset)}
                        </div>
                      </ComboboxOption>
                    );
                  })}
                </ComboboxOptions>
              )}

              {query !== "" &&
                query.length >= 2 &&
                searchResults.length === 0 &&
                !isLoading && (
                  <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
                    No assets found. Try a different search term.
                  </div>
                )}

              {query.length < 2 && !isLoading && (
                <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
                  Type at least 2 characters to search for assets.
                </div>
              )}
            </Combobox>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
}
